import React from "react";
import { map } from "lodash";
import styled from "styled-components";
import { localize, shorten } from "@backpacker/number";
import {
  FontSize,
  Radius,
  Spacing,
  content,
  ellipsis,
  form,
  transparentize,
} from "@glossy";
import { getMetricLabel } from "@linkfluence/i18n";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { Platform } from "@linkfluence/models/platform";
import { Timezone } from "@linkfluence/models/timezone";
import Rating from "@ui-kit/rating";
import Tooltip from "@ui-kit/tooltip";
import Attachment from "@radarly/components/post/attachments";
import Author from "@radarly/components/post/author";
import Content from "@radarly/components/post/content";
import { tr } from "@radarly/i18n";
import { Post, PostLens, PostLensFactory } from "@radarly/models/posts";
import { Project } from "@radarly/models/project";

interface IProps {
  post: Post & { isPhantom?: boolean };
  project?: Project;
  locale?: Locale;
  timezone: Timezone;
  isNarrow: boolean;
  width: number;
  style?: any;
  isTooltipDisabled?: boolean;
  onClick: (e: React.MouseEvent, url: string) => void;
}

const Root = styled.div<{ isNarrow?: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.isNarrow ? "column" : "row")};
  gap: ${Spacing.Small};
  width: 100%;
  height: 90px;
  border-top: 1px solid ${(props) => content(props, "tertiary")};
  padding-top: ${({ isNarrow }) => isNarrow && Spacing.Small};
  color: ${content};

  &:hover {
    text-decoration: none;
    background-color: ${(props) =>
      transparentize(0.85, form(props, "primary"))};
  }
`;

const AuthorContainer = styled.div<{ isNarrow?: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.isNarrow ? "column" : "row")};
  gap: ${Spacing.Medium};
  width: ${(props) => (props.isNarrow ? "auto" : "260px")};
`;

const ContentContainer = styled.div<{ isNarrow?: boolean; hasUrl: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.isNarrow ? "column" : "row")};
  align-items: center;
  gap: ${Spacing.Medium};
  width: ${(props) => (props.isNarrow ? "100%" : `calc(100% - 272px)`)};
  cursor: ${({ hasUrl }) => (hasUrl ? "pointer" : "auto")};
`;

const Metrics = styled.div`
  font-size: ${FontSize.Small};
  color: ${(props) => content(props, "secondary")};
  gap: ${Spacing.ExtraSmall};
  ${ellipsis()};
  display: flex;
`;

const MetricDiv = styled.div`
  display: inline;
`;

const FlexContainer = styled.div`
  gap: ${Spacing.ExtraSmall};
  display: flex;
  width: 100%;
  align-items: flex-start;
`;

const InlineContainer = styled.div<{ isNarrow?: boolean; attachment: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${(props) => (props.attachment ? `calc(100% - 44px)` : "100%")};
`;

const AttachmentPlaceholder = styled.div`
  height: 32px;
  width: 32px;
  background-color: ${(props) => content(props, "tertiary")};
  border-radius: ${Radius.Small};
`;

const ContentPlaceholder = styled.div`
  height: 20px;
  background-color: ${(props) => content(props, "tertiary")};
`;

const StyledContent = styled(Content)`
  div {
    ${ellipsis()};
  }
`;

const RadarlyPostWidgetPost = ({
  post,
  project,
  locale = Locale.English,
  timezone,
  isNarrow,
  width,
  style,
  isTooltipDisabled = false,
  onClick,
}: IProps) => {
  const lens = PostLensFactory.create(post);

  const isReview = PostLens.isPlatform(Platform.Review, post);

  const permalink = lens.getPermalink();
  const postMetrics = lens.getMetrics();
  const attachment = lens.getCover();
  const largePreview = !!lens.getLargePreviewData();
  const displayThumb = largePreview || attachment !== "";

  return (
    <Root
      style={style}
      isNarrow={isNarrow}
      onClick={(e) => onClick(e, permalink)}
    >
      <AuthorContainer isNarrow={isNarrow}>
        <Author
          showAvatar={!isNarrow}
          showPlatform
          post={post}
          locale={locale}
          timezone={timezone}
          width={width}
          isTooltipDisabled
        />
      </AuthorContainer>
      <ContentContainer
        isNarrow={isNarrow}
        hasUrl={typeof permalink === "string"}
      >
        <FlexContainer>
          {isTooltipDisabled ? (
            <AttachmentPlaceholder />
          ) : (
            <Attachment
              post={post}
              width={32}
              height={32}
              project={project}
              largePreview={false}
            />
          )}
          <InlineContainer isNarrow={isNarrow} attachment={displayThumb}>
            {isTooltipDisabled ? (
              <ContentPlaceholder />
            ) : (
              <StyledContent
                post={post}
                parse
                limit={280}
                small
                locale={locale}
              />
            )}
            <Metrics>
              {isReview && (
                <Rating
                  value={postMetrics.rating || 0}
                  size={12}
                  locale={locale}
                  isTooltipDisabled={isTooltipDisabled}
                />
              )}
              {!isReview &&
                map(
                  postMetrics,
                  (pm, key) =>
                    pm > 0 && (
                      <Tooltip
                        disabled={isTooltipDisabled}
                        tooltip={
                          getMetricLabel(locale, key as Metric) +
                          tr(locale, "PUNCTUATION::COLON") +
                          " " +
                          localize(locale, pm)
                        }
                        key={key}
                      >
                        <MetricDiv>
                          {getMetricLabel(locale, key as Metric)}
                          {tr(locale, "PUNCTUATION::COLON")}&nbsp;
                          {shorten(locale, pm)}
                        </MetricDiv>
                      </Tooltip>
                    )
                )}
            </Metrics>
          </InlineContainer>
        </FlexContainer>
      </ContentContainer>
    </Root>
  );
};

export default RadarlyPostWidgetPost;
